const DEFAULT_API_BASE = '/app-api'

export interface ApiEnvelope<T> {
  code: number
  data: T
  msg: string
}

export interface AuthTokens {
  userId: number
  accessToken: string
  refreshToken: string
  expiresTime: string | number
  openid?: string
}

export type NovaWechatLoginAction = 'LOGGED_IN' | 'CONFIRM_ACCOUNT'
export type NovaWechatPairingStatus = 'PENDING' | 'EMAIL_REQUIRED' | 'CANCELLED' | 'EXPIRED' | 'LOGGED_IN'

export interface NovaWechatLoginResponse {
  nextAction: NovaWechatLoginAction
  login?: AuthTokens
  pairingId?: string
  pollToken?: string
  miniProgramCodeBase64?: string
  expiresAt?: string | number
}

export interface NovaWechatPairingStatusResponse {
  status: NovaWechatPairingStatus
  login?: AuthTokens
  expiresAt?: string | number
}

export interface NovaSsoTicket {
  ticket: string
  audience: string
  expiresAt: string | number
  launchUrl: string
}

export interface MemberProfile {
  id: number
  nickname: string
  avatar?: string
  title?: string
  mobile?: string
  email?: string
  userType?: string
  contractorType?: string
  certificationStatus?: number
}

export interface PageResult<T> {
  total: number
  list: T[]
}

export interface ProjectSummary {
  id: number
  projectNo?: string
  title: string
  categoryName?: string
  status?: number
  budgetMin?: number | string
  budgetMax?: number | string
  viewCount?: number
  applicationCount?: number
  favoriteCount?: number
  mediaUrls?: string[]
  createTime?: string
  acceptingBid?: boolean
  publisherId?: number
  publisherNickname?: string
  publisherAvatar?: string
}

export interface ProjectDetail extends ProjectSummary {
  description?: string
  files?: string[]
  budgetRange?: string
  budgetType?: number
  validApplicationCount?: number
  hasApplied?: boolean
  isFavorite?: boolean
  publisher?: {
    userId: number
    nickname?: string
    avatar?: string
    userType?: number
  }
}

export interface ProjectListParams {
  pageNo?: number
  pageSize?: number
  industryName?: string
  budgetMin?: number
  budgetMax?: number
  keyword?: string
  orderBy?: 'createTime' | 'viewCount' | 'applicationCount'
  orderDirection?: 'asc' | 'desc'
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly code: number | 'NETWORK' | 'INVALID_RESPONSE',
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

const apiBase = (import.meta.env.VITE_NOVA_API_BASE || DEFAULT_API_BASE).replace(/\/$/, '')

async function request<T>(path: string, init: RequestInit = {}, accessToken?: string): Promise<T> {
  let response: Response
  const hasJsonBody = init.body !== undefined && !(init.body instanceof FormData)
  try {
    response = await fetch(`${apiBase}${path}`, {
      ...init,
      headers: {
        ...(hasJsonBody ? { 'Content-Type': 'application/json' } : {}),
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError('网络连接失败，请稍后重试', 'NETWORK')
  }

  let envelope: ApiEnvelope<T>
  try {
    envelope = await response.json() as ApiEnvelope<T>
  } catch {
    throw new ApiError(`接口返回无法解析（HTTP ${response.status}）`, 'INVALID_RESPONSE')
  }

  if (!envelope || typeof envelope !== 'object' || typeof envelope.code !== 'number') {
    throw new ApiError('接口返回格式不正确', 'INVALID_RESPONSE')
  }
  if (!response.ok || envelope.code !== 0) {
    throw new ApiError(envelope.msg || `请求失败（HTTP ${response.status}）`, envelope.code || response.status)
  }
  return envelope.data
}

const postJson = (body?: unknown): RequestInit => ({
  method: 'POST',
  ...(body === undefined ? {} : { body: JSON.stringify(body) }),
})

export const novaApi = {
  getProjects(params: ProjectListParams = {}) {
    const query = new URLSearchParams()
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== '') query.set(key, String(value))
    })
    return request<PageResult<ProjectSummary>>(`/member/project/list?${query}`)
  },
  getProject(id: number) {
    return request<ProjectDetail>(`/member/project/get/${encodeURIComponent(String(id))}`)
  },
  getWechatRedirect(redirectUri: string) {
    const query = new URLSearchParams({ type: '32', redirectUri })
    return request<string>(`/member/auth/social-auth-redirect?${query}`)
  },
  novaWechatLogin(code: string, state: string) {
    return request<NovaWechatLoginResponse>('/member/auth/nova-sso/wechat/login', postJson({ type: 32, code, state }))
  },
  getNovaWechatPairingStatus(pairingId: string, pollToken: string) {
    return request<NovaWechatPairingStatusResponse>(
      '/member/auth/nova-sso/wechat/pairing-status',
      postJson({ pairingId, pollToken }),
    )
  },
  sendNovaWechatPairingEmailCode(pairingId: string, pollToken: string, email: string) {
    return request<boolean>(
      '/member/auth/nova-sso/wechat/email-code',
      postJson({ pairingId, pollToken, email }),
    )
  },
  bindNovaWechatPairingEmail(pairingId: string, pollToken: string, email: string, code: string) {
    return request<NovaWechatPairingStatusResponse>(
      '/member/auth/nova-sso/wechat/email-bind',
      postJson({ pairingId, pollToken, email, code }),
    )
  },
  refreshToken(refreshToken: string) {
    const query = new URLSearchParams({ refreshToken })
    return request<AuthTokens>(`/member/auth/refresh-token?${query}`, postJson())
  },
  logout(accessToken: string) {
    return request<boolean>('/member/auth/logout', postJson(), accessToken)
  },
  getCurrentUser(accessToken: string) {
    return request<MemberProfile>('/member/user/get', {}, accessToken)
  },
  createNovaSsoTicket(accessToken: string) {
    return request<NovaSsoTicket>('/member/auth/nova-sso/ticket', postJson(), accessToken)
  },
  initMultipart(payload: { name: string; directory: 'portfolio'; contentType: string; size: number }, accessToken: string) {
    return request<{ sessionId: string; partSize: number; partCount: number; expiresAt: number }>('/infra/file/multipart/init', postJson(payload), accessToken)
  },
  getPartUrl(payload: { sessionId: string; partNumber: number }, accessToken: string) {
    return request<{ uploadUrl: string; expiresAt: number }>('/infra/file/multipart/part-url', postJson(payload), accessToken)
  },
  completeMultipart(payload: { sessionId: string; parts: Array<{ partNumber: number; etag: string }> }, accessToken: string) {
    return request<string>('/infra/file/multipart/complete', postJson(payload), accessToken)
  },
  abortMultipart(sessionId: string, accessToken: string) {
    return request<boolean>('/infra/file/multipart/abort', postJson({ sessionId }), accessToken)
  },
  createPortfolio(payload: Record<string, unknown>, accessToken: string) {
    return request<number>('/member/portfolio/create', postJson(payload), accessToken)
  },
  getOwnPortfolio(id: number, accessToken: string) {
    return request<{ id: number; auditStatus?: number }>(`/member/portfolio/get?id=${encodeURIComponent(id)}`, {}, accessToken)
  },
}
